import { useEffect, useState } from "react";
import { MapPin, X } from "lucide-react";
import { useLocation } from "react-router-dom";

/**
 * Soft "GateMate is DFW-first" banner.
 * - Uses `Intl.DateTimeFormat().resolvedOptions().timeZone` as a rough
 *   proxy for the visitor's location. Central Time ("America/Chicago")
 *   covers DFW so we suppress the banner for those visitors.
 * - Dismissible once — writes `gm_dfw_banner_dismissed=1` to localStorage
 *   so it never bothers them again on the same browser.
 * - Also honors the returning-visitor state — anyone who already joined the
 *   waitlist (gm_waitlist present in localStorage) doesn't need re-nudging.
 * - Never touches waitlist funnel; just a 1-line clarifier.
 */
export default function DfwLocationBanner() {
  const [show, setShow] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    try {
      if (localStorage.getItem("gm_dfw_banner_dismissed")) return;
      if (localStorage.getItem("gm_waitlist")) return; // already on the list
      const tz =
        Intl.DateTimeFormat().resolvedOptions().timeZone || "";
      // Central Time covers DFW. Anything else = show the banner.
      if (tz === "America/Chicago") return;
      // Oct 2026 hero brief: never on first load — only after the visitor
      // scrolls past the hero — and never while the cookie consent banner
      // is still undecided (they'd stack in the same corner).
      const evaluate = () => {
        const consentDecided = !!localStorage.getItem("gm_consent");
        const pastHero = window.scrollY > window.innerHeight * 0.85;
        setShow(consentDecided && pastHero);
      };
      window.addEventListener("scroll", evaluate, { passive: true });
      evaluate();
      return () => window.removeEventListener("scroll", evaluate);
    } catch (_) {
      /* no-op — never break the site if localStorage / Intl misbehave */
    }
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem("gm_dfw_banner_dismissed", "1");
    } catch (_) {}
    setShow(false);
  };

  if (!show) return null;
  if (pathname.startsWith("/app") || pathname.startsWith("/e/")) return null;
  // Long-form legal/content pages: the floating card overlaps the text column
  // on desktop — these pages are compliance surfaces, not growth surfaces.
  const LEGAL_PATHS = ["/privacy-policy", "/terms", "/community-guidelines", "/child-safety", "/delete-account"];
  if (LEGAL_PATHS.some((p) => pathname.startsWith(p))) return null;

  return (
    <div
      role="status"
      data-testid="dfw-location-banner"
      className="fixed inset-x-4 bottom-4 md:left-auto md:right-6 md:bottom-[10.5rem] md:max-w-md z-[65]"
    >
      <div className="glass rounded-2xl border border-neon-cyan/25 shadow-[0_0_40px_rgba(0,198,174,0.15)] p-4 md:p-5 flex items-start gap-3">
        <div className="grid place-items-center w-9 h-9 rounded-xl bg-neon-cyan/15 text-neon-cyan shrink-0">
          <MapPin className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-white font-semibold leading-snug">
            GateMate works wherever your night is.
          </p>
          <p className="text-xs text-zinc-400 mt-1 leading-relaxed">
            You're outside Dallas–Fort Worth — no problem, the app is free for everyone, everywhere. Venue partnerships start in DFW, with major travel hubs next.
          </p>
        </div>
        <button
          onClick={dismiss}
          data-testid="dfw-banner-dismiss"
          aria-label="Dismiss"
          className="grid place-items-center w-7 h-7 rounded-full bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-white transition-colors shrink-0"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
